import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShield,
  FiInfo,
  FiSliders,
  FiSlash,
  FiLayers,
  FiGlobe,
  FiHelpCircle,
  FiMail,
  FiCalendar,
  FiUserCheck,
  FiLock,
  FiEdit3,
  FiBarChart2,
  FiShare2,
  FiFileText,
  FiSettings,
  FiChevronRight,
  FiArrowUp,
  FiList
} from 'react-icons/fi';
import './CookiePolicy.css';

const TOC_ITEMS = [
  { id: 'what-are-cookies', label: '1. What Are Cookies?', icon: FiInfo },
  { id: 'how-we-use-cookies', label: '2. How We Use Cookies', icon: FiSliders },
  { id: 'disabling-cookies', label: '3. Disabling Cookies', icon: FiSlash },
  { id: 'the-cookies-we-set', label: '4. The Cookies We Set', icon: FiLayers },
  { id: 'third-party-cookies', label: '5. Third Party Cookies', icon: FiGlobe },
  { id: 'more-information', label: '6. More Information', icon: FiHelpCircle },
  { id: 'note-related-policies', label: '7. Note & Related Policies', icon: FiShield },
];

const CookiePolicy = () => {
  const [activeSection, setActiveSection] = useState('what-are-cookies');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }

      // Determine active section based on scroll position
      const scrollPosition = window.scrollY + 180;
      for (let i = TOC_ITEMS.length - 1; i >= 0; i--) {
        const section = document.getElementById(TOC_ITEMS[i].id);
        if (section) {
          const top = section.offsetTop;
          if (scrollPosition >= top) {
            setActiveSection(TOC_ITEMS[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90; // Offset for navbar
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="cookie-page-wrapper">
      {/* Background Decorative Mesh Orbs */}
      <div className="cookie-bg-ambient" aria-hidden="true">
        <div className="cookie-ambient-orb orb-primary"></div>
        <div className="cookie-ambient-orb orb-secondary"></div>
        <div className="cookie-ambient-orb orb-tertiary"></div>
      </div>

      {/* Hero Header Section */}
      <section className="cookie-hero-section">
        <div className="cookie-hero-container">
          <div className="cookie-hero-content">
            {/* Pill Badge */}
            <div className="cookie-hero-badge">
              <FiShield className="cookie-badge-icon" />
              <span>Legal</span>
            </div>

            {/* Gradient Title */}
            <h1 className="cookie-hero-title">
              <span className="title-cookie">Cookie</span> <span className="title-policy">Policy</span>
            </h1>

            {/* Subtitle / Intro Description */}
            <p className="cookie-hero-subtitle">
              This Cookie Policy explains how and why I use cookies and similar technologies on my portfolio website.
            </p>

            {/* Metadata Pill */}
            <div className="cookie-hero-meta">
              <div className="cookie-meta-item">
                <FiCalendar className="cookie-meta-icon" />
                <span>
                  <strong>Last Updated On: </strong>28-03-2026
                </span>
              </div>
            </div>
          </div>

          {/* Hero Right Visual: High-tech Cookie & Shield Illustration */}
          <div className="cookie-hero-visual" aria-hidden="true">
            <div className="cookie-shield-canvas">
              {/* Central Glowing Backdrop */}
              <div className="cookie-glow-backdrop"></div>

              <svg className="cookie-shield-svg" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Orbit track rings */}
                <circle cx="140" cy="140" r="115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 6" className="cookie-orbit-ring" />
                <circle cx="140" cy="140" r="85" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />

                {/* Connector rays */}
                <line x1="140" y1="140" x2="225" y2="70" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="250" y2="155" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="65" y2="205" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />

                <defs>
                  {/* Cookie gradients */}
                  <radialGradient id="cookieGrad" cx="130" cy="120" r="60" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="60%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#d97706" />
                  </radialGradient>
                  {/* Shield gradients */}
                  <linearGradient id="cookieShieldGrad" x1="150" y1="130" x2="200" y2="215" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="50%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#1e3a8a" />
                  </linearGradient>
                  <filter id="cookieDropShadow" x="65" y="55" width="140" height="140" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#d97706" floodOpacity="0.35" />
                  </filter>
                  <filter id="cookieShieldShadow" x="135" y="115" width="80" height="105" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#2563eb" floodOpacity="0.45" />
                  </filter>
                </defs>

                {/* Golden Cookie with Chocolate Chips in Background */}
                <g filter="url(#cookieDropShadow)">
                  <circle cx="130" cy="120" r="54" fill="url(#cookieGrad)" stroke="#fde68a" strokeWidth="2.5" />
                  {/* Chocolate chip spots */}
                  <circle cx="106" cy="98" r="5.5" fill="#78350f" />
                  <circle cx="145" cy="88" r="4.5" fill="#78350f" />
                  <circle cx="120" cy="128" r="6" fill="#78350f" />
                  <circle cx="95" cy="132" r="5" fill="#78350f" />
                  <circle cx="152" cy="122" r="5" fill="#78350f" />
                  <circle cx="118" cy="155" r="4" fill="#78350f" />
                </g>

                {/* Glowing Shield in Foreground */}
                <g filter="url(#cookieShieldShadow)">
                  <path
                    d="M174 130 L204 142 C204 177 190 203 174 215 C158 203 144 177 144 142 L174 130 Z"
                    fill="url(#cookieShieldGrad)"
                    stroke="#60a5fa"
                    strokeWidth="2.5"
                  />
                  {/* Inner padlock in shield */}
                  <path
                    d="M168 171 V163 C168 159.7 170.7 157 174 157 C177.3 157 180 159.7 180 163 V171"
                    stroke="#ffffff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <rect x="164" y="170" width="20" height="17" rx="3" fill="#ffffff" />
                  <circle cx="174" cy="177" r="2" fill="#1e3a8a" />
                  <path d="M174 179 V183" stroke="#1e3a8a" strokeWidth="1.5" strokeLinecap="round" />
                </g>
              </svg>

              {/* Floating Node 1: Teal Document Circle */}
              <div className="cookie-floating-bubble bubble-teal" style={{ top: '18%', right: '14%' }}>
                <FiFileText className="bubble-icon" />
              </div>

              {/* Floating Node 2: Blue Globe Circle */}
              <div className="cookie-floating-bubble bubble-blue" style={{ top: '50%', right: '0%' }}>
                <FiGlobe className="bubble-icon" />
              </div>

              {/* Floating Node 3: Purple Gear Circle */}
              <div className="cookie-floating-bubble bubble-purple" style={{ bottom: '15%', left: '12%' }}>
                <FiSettings className="bubble-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Layout with Sidebar & Content Cards */}
      <div className="cookie-body-container">
        {/* Mobile Table of Contents Accordion Toggle */}
        <div className="cookie-mobile-toc-toggle">
          <button
            className="mobile-toc-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
          >
            <FiList className="toc-toggle-icon" />
            <span>On This Page: {TOC_ITEMS.find((item) => item.id === activeSection)?.label || 'Sections'}</span>
            <FiChevronRight className={`toc-toggle-arrow ${mobileMenuOpen ? 'open' : ''}`} />
          </button>
        </div>

        {/* Left Column: Sticky Table of Contents */}
        <aside className={`cookie-sidebar-nav ${mobileMenuOpen ? 'mobile-visible' : ''}`}>
          <div className="cookie-toc-card">
            <h2 className="cookie-toc-title">On This Page</h2>
            <nav className="cookie-toc-list" aria-label="Table of Contents">
              {TOC_ITEMS.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`cookie-toc-item ${isActive ? 'active' : ''}`}
                    type="button"
                  >
                    <span className="toc-item-indicator" aria-hidden="true" />
                    <IconComponent className="toc-item-icon" />
                    <span className="toc-item-text">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Right Column: Main Content Cards */}
        <main className="cookie-content-stream">
          {/* Section 1: What Are Cookies? */}
          <section id="what-are-cookies" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-amber">
                <FiInfo className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                1. What Are <span className="title-gradient">Cookies?</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                As is common practice with almost all professional websites, this site uses cookies, which are tiny files that are downloaded to your computer, to improve your experience. This page describes what information they gather, how we use it, and why we sometimes need to store these cookies. We will also share how you can prevent these cookies from being stored, although this may downgrade or 'break' certain elements of the site's functionality.
              </p>
            </div>
          </section>

          {/* Section 2: How We Use Cookies */}
          <section id="how-we-use-cookies" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-blue">
                <FiSliders className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                2. How We Use <span className="title-gradient">Cookies</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                We use cookies for a variety of reasons detailed below. Unfortunately, in most cases, there are no industry-standard options for disabling cookies without completely disabling the functionality and features they add to this site. It is recommended that you leave on all cookies if you are not sure whether you need them or not, in case they are used to provide a service that you use.
              </p>
            </div>
          </section>

          {/* Section 3: Disabling Cookies */}
          <section id="disabling-cookies" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-rose">
                <FiSlash className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                3. Disabling <span className="title-gradient">Cookies</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                You can prevent the setting of cookies by adjusting the settings on your browser (see your browser Help for how to do this). Be aware that disabling cookies will affect the functionality of this and many other websites that you visit. Disabling cookies will usually result in also disabling certain functionality and features of the site. Therefore, it is recommended that you do not disable cookies.
              </p>
            </div>
          </section>

          {/* Section 4: The Cookies We Set */}
          <section id="the-cookies-we-set" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-purple">
                <FiLayers className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                4. The Cookies We <span className="title-gradient">Set</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <div className="cookie-category-grid">
                {/* Account related cookies */}
                <div className="cookie-category-card card-account">
                  <div className="cookie-category-header">
                    <div className="cookie-badge badge-blue">
                      <FiUserCheck />
                    </div>
                    <h3 className="cookie-category-title">Account related cookies</h3>
                  </div>
                  <p className="cookie-category-desc">
                    If you create an account with us, then we will use cookies for the management of the signup process and general administration.
                  </p>
                </div>

                {/* Login related cookies */}
                <div className="cookie-category-card card-login">
                  <div className="cookie-category-header">
                    <div className="cookie-badge badge-purple">
                      <FiLock />
                    </div>
                    <h3 className="cookie-category-title">Login related cookies</h3>
                  </div>
                  <p className="cookie-category-desc">
                    We use cookies when you are logged in so that we can remember this fact. This prevents you from having to log in every single time you visit a new page.
                  </p>
                </div>

                {/* Forms related cookies */}
                <div className="cookie-category-card card-forms">
                  <div className="cookie-category-header">
                    <div className="cookie-badge badge-amber">
                      <FiEdit3 />
                    </div>
                    <h3 className="cookie-category-title">Forms related cookies</h3>
                  </div>
                  <p className="cookie-category-desc">
                    When you submit data to through a form such as those found on contact pages or comment forms, cookies may be set to remember your user details for future correspondence.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Third Party Cookies */}
          <section id="third-party-cookies" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-cyan">
                <FiGlobe className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                5. Third Party <span className="title-gradient">Cookies</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                In some special cases, we also use cookies provided by trusted third parties. The following section details which third party cookies you might encounter through this site.
              </p>

              <div className="third-party-list">
                <div className="third-party-item">
                  <div className="third-party-icon-box box-analytics">
                    <FiBarChart2 />
                  </div>
                  <div className="third-party-content">
                    <p className="cookie-paragraph" style={{ marginBottom: 0 }}>
                      This site uses Google Analytics which is one of the most widespread and trusted analytics solutions on the web for helping us to understand how you use the site and ways that we can improve your experience.
                    </p>
                  </div>
                </div>

                <div className="third-party-item">
                  <div className="third-party-icon-box box-metrics">
                    <FiLayers />
                  </div>
                  <div className="third-party-content">
                    <p className="cookie-paragraph" style={{ marginBottom: 0 }}>
                      Third party analytics are used to track and measure usage of this site so that we can continue to produce engaging content.
                    </p>
                  </div>
                </div>

                <div className="third-party-item">
                  <div className="third-party-icon-box box-social">
                    <FiShare2 />
                  </div>
                  <div className="third-party-content">
                    <p className="cookie-paragraph" style={{ marginBottom: 0 }}>
                      We also use social media buttons and/or plugins on this site that allow you to connect with your social network in various ways. These will set cookies through our site which may be used to enhance your profile on their site.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: More Information */}
          <section id="more-information" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-teal">
                <FiHelpCircle className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                6. More <span className="title-gradient">Information</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                Hopefully, that has clarified things for you and as was previously mentioned if there is something that you aren't sure whether you need or not, it's usually safer to leave cookies enabled in case it does interact with one of the features you use on our site.
              </p>
              <p className="cookie-paragraph">
                If you are still looking for more information, you can contact us through one of our preferred contact methods:
              </p>

              <div className="cookie-contact-card">
                <div className="contact-card-icon-box">
                  <FiMail className="contact-mail-icon" />
                </div>
                <div className="contact-card-details">
                  <div className="contact-card-label">By visiting this page on our website:</div>
                  <a
                    href="https://praveen-kumar-b.web.app/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-card-button"
                  >
                    <span>Contact If Any Queries</span>
                    <FiChevronRight className="btn-arrow" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Note & Related Policies */}
          <section id="note-related-policies" className="cookie-card">
            <div className="cookie-card-header">
              <div className="cookie-section-icon-box box-purple">
                <FiShield className="cookie-section-icon" />
              </div>
              <h2 className="cookie-card-title">
                7. Note & Related <span className="title-gradient">Policies</span>
              </h2>
            </div>

            <div className="cookie-card-body">
              <p className="cookie-paragraph">
                You Can View Our <strong>Privacy Policy</strong>, <strong>Terms & Conditions</strong>, and <strong>Cookie Policy</strong> Below.
              </p>

              <div className="related-policies-grid">
                <Link to="/privacy-policy" className="policy-link-card">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Privacy Policy</span>
                    <span className="policy-sub">Data & Privacy Protection</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>

                <Link to="/terms-conditions" className="policy-link-card">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Terms & Conditions</span>
                    <span className="policy-sub">Service Usage Guidelines</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>

                <Link to="/cookie-policy" className="policy-link-card active">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Cookie Policy</span>
                    <span className="policy-sub">Current Document</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Floating Back-To-Top Button */}
      {showBackToTop && (
        <button
          className="cookie-back-to-top"
          onClick={scrollToTop}
          title="Back to Top"
          aria-label="Back to Top"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default CookiePolicy;
